import { curOf, moneyExact, plain, wfmt } from './money';
import { coverersOf, paymentsFor, tripFacts, unitWeightTotal } from './split';
import type { Expense, TripData, Unit } from '../types';

/**
 * Three export formats, all built from one tripFacts() pass so an export can
 * never disagree with what the screens showed.
 */

export const CATEGORY_LABELS: Record<string, string> = {
  stay: 'Stay', food: 'Food', groceries: 'Groceries', travel: 'Travel',
  activity: 'Activity', drinks: 'Drinks', shopping: 'Shopping', other: 'Other'
};

const catLabel = (id: string) => CATEGORY_LABELS[id] || 'Other';
const methodName = (b: Expense['basis']) =>
  b === 'perUnit' ? 'PER FAMILY' : b === 'custom' ? 'CUSTOM' : 'PER PERSON';

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'trip';

/* =========================================================================
   1. AI audit
   ========================================================================= */

/**
 * Built to be pasted into ChatGPT, Claude or Gemini.
 *
 * The structure matters more than the content: the rules and the raw ledger
 * come first, the app's own answers come last and are clearly labelled as the
 * thing under test. That way the model derives the numbers itself and then
 * compares, instead of being handed a conclusion and asked whether it looks
 * fine — a question models are bad at answering honestly.
 */
export function buildAudit(data: TripData): string {
  const { group, units, settlements } = data;
  const f = tripFacts(data);
  const c = curOf(group.currency);
  const L: string[] = [];

  L.push('EVENHOUSE - SHARED EXPENSE AUDIT');
  L.push(`Trip: ${group.name}${group.when ? `  (${group.when})` : ''}`);
  L.push(`Currency: ${c.code} (${c.symbol}).  1 ${c.code} = 100 minor units.`);
  L.push(`Exported: ${new Date().toISOString().slice(0, 10)}`);
  L.push('');
  L.push('=== WHAT I NEED FROM YOU ===');
  L.push("Recompute this trip's expense split yourself from RAW LEDGER below, then compare");
  L.push('your results with APP RESULTS. Work in whole minor units (integers), not decimals,');
  L.push('so floating-point error cannot creep in. Then report:');
  L.push('  1. Any expense whose split does not sum to exactly the expense amount.');
  L.push('  2. Any household whose net balance differs from the app figure.');
  L.push('  3. Whether all net balances sum to exactly zero.');
  L.push('  4. Whether the settle-up plan clears every household to zero, and whether a');
  L.push('     plan with fewer transfers exists.');
  L.push('For each disagreement give: the item, your figure, the app figure, the difference.');
  L.push('If everything matches, say so in one line and stop. Do not restate the ledger.');
  L.push('');
  L.push('=== THE SPLITTING RULES ===');
  L.push('* Money settles between HOUSEHOLDS, not individuals. A couple or a family is one');
  L.push('  household with one balance. Members of the same household never owe each other.');
  L.push('* Each person carries a share weight: 1 (full), 0.5 (half, typically an older');
  L.push("  child) or 0 (not counted). A household's weight is the sum of its people's.");
  L.push('* Each expense has one paying household. Sometimes other households already covered');
  L.push('  part of it (a coupon or gift card they bought earlier); then "paid by" lists each');
  L.push("  household's part, and those parts sum to the amount. Covering part of an expense");
  L.push('  changes who is credited with paying, never how it splits. Each expense is split by');
  L.push('  one of three methods:');
  L.push('    PER PERSON - weight of each household = summed weights of ITS listed');
  L.push('                 participants. Divide the amount in proportion to those weights.');
  L.push('    PER FAMILY - every listed household gets weight 1, regardless of size.');
  L.push('    CUSTOM     - exact amounts are stated per household.');
  L.push("* ROUNDING (largest remainder): compute each household's exact share, floor it to");
  L.push('  whole minor units, then hand out the leftover minor units one at a time to the');
  L.push('  households with the largest fractional remainders (ties broken by household id).');
  L.push('  The parts must sum to exactly the expense amount.');
  L.push('* NET BALANCE of a household =');
  L.push('    (total it paid for expenses, including parts it covered)');
  L.push('  - (sum of its shares of all expenses)');
  L.push('  + (settlement payments it made)');
  L.push('  - (settlement payments it received)');
  L.push('  A positive net means the household is owed money.');
  L.push('');
  L.push('=== RAW LEDGER ===');
  L.push('');
  L.push('HOUSEHOLDS  [total weight]');
  for (const u of units) {
    L.push(`  ${f.hid[u.id]}  ${u.name}  [${unitWeightTotal(u)}]`);
    L.push('        members: ' + u.people
      .map((p) => `${p.name}=${p.weight}${p.isChild ? ` (child${p.age ? `, ${p.age}` : ''})` : ''}`)
      .join(', '));
  }
  L.push('');
  L.push(`EXPENSES  (amounts in ${c.code})`);
  f.rows.forEach(({ expense: e }, i) => {
    L.push(`  E${i + 1}  ${e.date}  "${e.desc}"`);
    L.push(`        amount: ${plain(e.amountMinor)}   paid by: ${payersText(units, e, (id) => f.hid[id] ?? '?', plain)}   category: ${catLabel(e.category)}`);
    L.push(`        method: ${methodName(e.basis)}`);
    L.push(`        participants: ${participantText(data, e, f.hid)}`);
    if (e.note) L.push(`        note: ${e.note}`);
  });
  L.push('');
  if (settlements.length) {
    L.push('SETTLEMENTS ALREADY PAID');
    settlements
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .forEach((s, i) => L.push(
        `  S${i + 1}  ${s.date}  ${f.hid[s.fromUnitId] ?? '?'} -> ${f.hid[s.toUnitId] ?? '?'}  ` +
        `${plain(s.amountMinor)}${s.method ? `  (${s.method})` : ''}`
      ));
  } else {
    L.push('SETTLEMENTS ALREADY PAID: none');
  }
  L.push('');
  L.push('=== APP RESULTS (these are what you are checking) ===');
  L.push('');
  L.push('PER-EXPENSE SPLIT');
  f.rows.forEach(({ expense: e, shares }, i) => {
    L.push(`  E${i + 1}  total ${plain(e.amountMinor)}  ->  ` + units
      .filter((u) => shares[u.id])
      .map((u) => `${f.hid[u.id]} ${plain(shares[u.id])}`)
      .join(' | '));
  });
  L.push('');
  L.push('PER-HOUSEHOLD TOTALS');
  const nameCol = Math.max(9, ...units.map((u) => `${f.hid[u.id]} ${u.name}`.length));
  const pad = (v: string | number, n: number) => String(v).padStart(n);
  L.push('  ' + 'household'.padEnd(nameCol) + pad('weight', 8) + pad('paid', 12) +
    pad('owed share', 13) + pad('settled out', 14) + pad('settled in', 13) + pad('net', 13));
  for (const u of units) {
    L.push('  ' + `${f.hid[u.id]} ${u.name}`.padEnd(nameCol) +
      pad(unitWeightTotal(u), 8) + pad(plain(f.paid[u.id]), 12) + pad(plain(f.share[u.id]), 13) +
      pad(plain(f.sentOut[u.id]), 14) + pad(plain(f.received[u.id]), 13) + pad(plain(f.net[u.id]), 13));
  }
  L.push('  SUM OF ALL NET BALANCES: ' +
    plain(units.reduce((a, u) => a + f.net[u.id], 0)) + '   (must be exactly 0.00)');
  L.push(`  TRIP TOTAL: ${plain(f.totalMinor)}  across ${f.rows.length} expenses`);
  L.push('');
  L.push(`SETTLE-UP PLAN  (${f.plan.length} transfer${f.plan.length === 1 ? '' : 's'})`);
  if (f.plan.length) {
    for (const t of f.plan) {
      L.push(`  ${f.hid[t.from]} pays ${f.hid[t.to]}  ${plain(t.amountMinor)}`);
    }
  } else {
    L.push('  none - everyone is already settled');
  }
  L.push('');
  L.push('=== END ===');
  return L.join('\n');
}

/** "H1" — or, when others covered part of it, each household's part: "H1 66.00 + H2 15.00". */
function payersText(
  units: Unit[], e: Expense, label: (id: string) => string, amount: (m: number) => string
): string {
  const coverers = coverersOf(units, e);
  if (!coverers.length) return label(e.paidByUnitId);
  const pay = paymentsFor(e);
  return [e.paidByUnitId, ...coverers.map((u) => u.id)]
    .map((id) => `${label(id)} ${amount(pay[id])}`)
    .join(' + ');
}

function participantText(data: TripData, e: Expense, hid: Record<string, string>): string {
  const { units } = data;
  if (e.basis === 'perUnit') {
    return units.filter((u) => e.participantUnitIds.includes(u.id))
      .map((u) => hid[u.id]).join(', ');
  }
  if (e.basis === 'custom') {
    return units.filter((u) => e.customMinor?.[u.id])
      .map((u) => `${hid[u.id]}=${plain(e.customMinor[u.id])}`).join(', ');
  }
  return units.map((u) => {
    const inc = u.people.filter((p) => e.participantPersonIds.includes(p.id) && p.weight > 0);
    return inc.length ? `${hid[u.id]}(${inc.map((p) => `${p.name} ${p.weight}`).join(', ')})` : null;
  }).filter(Boolean).join('  ');
}

/* =========================================================================
   2. CSV
   ========================================================================= */

const csvCell = (v: unknown): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const csvRow = (a: unknown[]): string => a.map(csvCell).join(',');

/**
 * Each household gets its own column, which is the bit that makes this useful:
 * sum a family's column in a spreadsheet and it must equal their total share.
 */
export function buildCSV(data: TripData): string {
  const { group, units, settlements } = data;
  const f = tripFacts(data);
  const unitName = (id: string) => units.find((u) => u.id === id)?.name ?? '';
  const c = curOf(group.currency);
  const cur = ` (${c.code})`;
  const o: string[] = [];

  o.push(csvRow(['Evenhouse export', group.name, group.when, 'Currency', c.code]));
  o.push('');
  o.push(csvRow(['Date', 'Description', 'Category', 'Method', 'Paid by', 'Amount' + cur]
    .concat(units.map((u) => u.name + cur))));
  for (const { expense: e, shares } of f.rows) {
    o.push(csvRow(
      [e.date, e.desc, catLabel(e.category), methodName(e.basis),
       payersText(units, e, unitName, plain), plain(e.amountMinor)]
        .concat(units.map((u) => plain(shares[u.id] || 0)))
    ));
  }
  o.push(csvRow(['', '', '', '', 'Total', plain(f.totalMinor)]
    .concat(units.map((u) => plain(f.share[u.id])))));

  o.push('');
  o.push(csvRow(['Settlements']));
  o.push(csvRow(['Date', 'From', 'To', 'Amount' + cur, 'Method']));
  if (settlements.length) {
    for (const s of settlements.slice().sort((a, b) => a.date.localeCompare(b.date))) {
      o.push(csvRow([s.date,
        units.find((u) => u.id === s.fromUnitId)?.name ?? '',
        units.find((u) => u.id === s.toUnitId)?.name ?? '',
        plain(s.amountMinor), s.method || '']));
    }
  } else o.push(csvRow(['(none)']));

  o.push('');
  o.push(csvRow(['Net balances']));
  o.push(csvRow(['Household', 'Shares', 'Paid' + cur, 'Owed share' + cur,
                 'Settled out' + cur, 'Settled in' + cur, 'Net' + cur]));
  for (const u of units) {
    o.push(csvRow([u.name, unitWeightTotal(u), plain(f.paid[u.id]), plain(f.share[u.id]),
                   plain(f.sentOut[u.id]), plain(f.received[u.id]), plain(f.net[u.id])]));
  }
  o.push(csvRow(['Sum', '', '', '', '', '', plain(units.reduce((a, u) => a + f.net[u.id], 0))]));

  o.push('');
  o.push(csvRow(['Settle-up plan']));
  o.push(csvRow(['From', 'To', 'Amount' + cur]));
  if (f.plan.length) {
    for (const t of f.plan) {
      o.push(csvRow([units.find((u) => u.id === t.from)?.name ?? '',
                     units.find((u) => u.id === t.to)?.name ?? '', plain(t.amountMinor)]));
    }
  } else o.push(csvRow(['(all settled)']));

  return o.join('\n');
}

/* =========================================================================
   3. Statement
   ========================================================================= */

/** One table of the statement. */
export interface StatementSection {
  title: string;
  head: string[];
  /** Indexes of right-aligned figure columns. */
  numeric: number[];
  /** Short text columns (dates, "per person") kept on one line in the PDF. */
  nowrap?: number[];
  body: string[][];
  /** A bold closing row; its label spans every column before `values`. */
  total?: { label: string; values: string[] };
  /** Shown instead of the table when there are no rows. */
  empty?: string;
}

export interface StatementDoc {
  title: string;
  meta: string;
  sections: StatementSection[];
  foot: string;
}

/**
 * What the statement says, apart from how it's drawn. The preview in the export
 * sheet and the shared PDF both render this, so they can't disagree.
 */
export function statementContent(data: TripData): StatementDoc {
  const { group, units, settlements } = data;
  const f = tripFacts(data);
  const M = (m: number) => moneyExact(m, group.currency);
  const unitName = (id: string) => units.find((u) => u.id === id)?.name ?? '';
  const payments = f.plan.length;

  const sections: StatementSection[] = [
    {
      title: 'Where each household stands',
      head: ['Household', 'Shares', 'Paid', 'Its share', 'Net'],
      numeric: [1, 2, 3, 4],
      body: units.map((u) => [
        u.name, wfmt(unitWeightTotal(u)), M(f.paid[u.id]), M(f.share[u.id]),
        f.net[u.id] === 0 ? '—' : M(f.net[u.id])
      ]),
      total: {
        label: 'Total',
        values: ['', M(f.totalMinor), M(f.totalMinor), M(units.reduce((a, u) => a + f.net[u.id], 0))]
      }
    },
    {
      title: `Settle up — ${payments} payment${payments === 1 ? '' : 's'}`,
      head: ['From', 'To', 'Amount'],
      numeric: [2],
      body: f.plan.map((t) => [unitName(t.from), unitName(t.to), M(t.amountMinor)]),
      empty: 'Everyone is settled. Nothing to transfer.'
    },
    {
      title: 'Expenses',
      head: ['Date', 'Description', 'Paid by', 'Split', 'Amount'],
      numeric: [4],
      nowrap: [0, 3],
      body: f.rows.map(({ expense: e }) => [
        e.date, e.desc, payersText(units, e, unitName, M),
        e.basis === 'perUnit' ? 'per family' : e.basis === 'custom' ? 'custom' : 'per person',
        M(e.amountMinor)
      ]),
      total: { label: 'Trip total', values: [M(f.totalMinor)] }
    }
  ];
  if (settlements.length) {
    sections.push({
      title: 'Payments already made',
      head: ['Date', 'From', 'To', 'Method', 'Amount'],
      numeric: [4],
      nowrap: [0],
      body: settlements.slice().sort((a, b) => a.date.localeCompare(b.date)).map((s) => [
        s.date, unitName(s.fromUnitId), unitName(s.toUnitId), s.method ?? '', M(s.amountMinor)
      ])
    });
  }

  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return {
    title: group.name,
    meta: [group.when, `${units.length} households`, `${f.rows.length} expenses`,
      curOf(group.currency).code, `statement of ${today}`].filter(Boolean).join(' · '),
    sections,
    foot: 'Split between households, not individuals. Shares are weighted by each ' +
      "household's members (full share, half share, or not counted). Generated by Evenhouse."
  };
}

const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"]/g, (ch) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch] as string));

/** The statement as HTML, for the preview in the export sheet. */
export function buildStatement(data: TripData): string {
  const doc = statementContent(data);
  const H: string[] = ['<div class="doc">', `<h1>${esc(doc.title)}</h1>`, `<div class="meta">${esc(doc.meta)}</div>`];
  for (const s of doc.sections) {
    const cell = (tag: 'th' | 'td', i: number, v: string) =>
      `<${tag}${s.numeric.includes(i) ? ' class="n"' : ''}>${esc(v)}</${tag}>`;
    H.push(`<h2>${esc(s.title)}</h2>`);
    if (!s.body.length && s.empty) {
      H.push(`<p style="font-size:12px;margin:0">${esc(s.empty)}</p>`);
      continue;
    }
    H.push('<table><tr>' + s.head.map((h, i) => cell('th', i, h)).join('') + '</tr>');
    for (const row of s.body) H.push('<tr>' + row.map((v, i) => cell('td', i, v)).join('') + '</tr>');
    if (s.total) {
      const span = s.head.length - s.total.values.length;
      H.push(`<tr class="tot"><td${span > 1 ? ` colspan="${span}"` : ''}>${esc(s.total.label)}</td>` +
        s.total.values.map((v, k) => cell('td', span + k, v)).join('') + '</tr>');
    }
    H.push('</table>');
  }
  H.push(`<div class="foot">${esc(doc.foot)}</div></div>`);
  return H.join('');
}

/**
 * The statement as a PDF to share. jsPDF and the embedded font are a separate
 * chunk, fetched the first time someone opens the statement.
 */
export async function statementPdf(data: TripData): Promise<Blob> {
  const { renderStatementPdf } = await import('./statementPdf');
  return new Blob([renderStatementPdf(statementContent(data))], { type: 'application/pdf' });
}

/* =========================================================================
   delivery
   ========================================================================= */

function downloadFile(file: File) {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick; Safari needs the URL to survive the click.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Share sheet where it exists (so a file can go straight into WhatsApp),
 * download otherwise. Call it straight from a tap with the file already built:
 * iOS refuses a share that arrives seconds after the gesture.
 */
export async function shareFile(file: File): Promise<void> {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (nav.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: file.name });
      return;
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
    }
  }
  downloadFile(file);
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export const auditFilename = (name: string) => `${slug(name)}-audit.txt`;
export const csvFilename = (name: string) => `${slug(name)}-expenses.csv`;
export const statementFilename = (name: string) => `${slug(name)}-statement.pdf`;
